import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import { Add, Delete, Edit, ExpandMore as ExpandMoreIcon } from "@mui/icons-material";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";

import VCatKitService from "../../services/VcatKit/service";
import { useAtom } from "jotai";
import { accountRootsAtom, fetchAccountRootsAtom } from "../../state/atoms/accountRootsAtom";
import { KitStatusEnum } from "../../types/enums";
import ConfirmDialog from "../../components/ConfirmDialog";


const VCatKitsPage: React.FC = () => {
  const [kits, setKits] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [selectedKit, setSelectedKit] = useState<any | null>(null);
  const [formData, setFormData] = useState<any>({
    serial: "",
    eyetracker_type: "",
    account_root_id: "",
    headset_status: KitStatusEnum.WORKING,
  });

  const [accountRoots] = useAtom(accountRootsAtom);
  const [, fetchAccountRoots] = useAtom(fetchAccountRootsAtom);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [kitToDelete, setKitToDelete] = useState<number | null>(null);

const fetchKits = useCallback(async () => {
  try {
    setLoading(true);
    const data = await VCatKitService.getAllVCatKits();
    const enriched = data.map((kit: any) => {
      const rootName =
        accountRoots?.find((root) => root.id === kit.account_root_id)?.name || "Unknown";
      return { ...kit, account_root_name: rootName };
    });
    setKits(enriched);
  } catch (err) {
    console.error("Error fetching kits:", err);
    setError((err as Error).message);
  } finally {
    setLoading(false);
  }
}, [accountRoots]);


useEffect(() => {
  fetchAccountRoots();
}, [fetchAccountRoots]);

useEffect(() => {
  if (accountRoots) {
    fetchKits();
  }
}, [accountRoots, fetchKits]);


  const handleOpenCreate = () => {
    setIsEdit(false);
    setFormData({
      serial: "",
      eyetracker_type: "",
      account_root_id: "",
      headset_status: KitStatusEnum.WORKING,
    });
    setOpenDialog(true);
  };

  const handleOpenEdit = (kit: any) => {
    setIsEdit(true);
    setSelectedKit(kit);
    setFormData({
      serial: kit.serial,
      eyetracker_type: kit.eyetracker_type,
      account_root_id: kit.account_root_id,
      headset_status: kit.headset_status || KitStatusEnum.WORKING,
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedKit(null);
  };

  const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent<any>) => {
    const { name, value } = e.target;
    setFormData((prev: any) => ({ ...prev, [name!]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (isEdit && selectedKit) {
        await VCatKitService.updateVCatKit(selectedKit.id, formData);
      } else {
        await VCatKitService.createVCatKit(formData);
      }
      await fetchKits();
      handleCloseDialog();
    } catch (err) {
      console.error("Error saving VCat Kit:", err);
      alert((err as Error).message);
    }
  };

  const handleDeleteClick = (kitId: number) => {
    setKitToDelete(kitId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (kitToDelete !== null) {
      try {
        await VCatKitService.deleteVCatKit(kitToDelete);
        await fetchKits();
      } catch (err) {
        console.error("Error deleting kit:", err);
        alert((err as Error).message);
      } finally {
        setDeleteDialogOpen(false);
        setKitToDelete(null);
      }
    }
  };

  const groupedByRoot = useMemo(() => {
    return kits.reduce((acc: Record<string, any[]>, kit) => {
      const rootName = kit.account_root_name;
      if (!acc[rootName]) acc[rootName] = [];
      acc[rootName].push(kit);
      return acc;
    }, {});
  }, [kits]);

  return (
    <Box sx={{ color: "#fff", p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
        <Typography variant="h4">VCAT Kits</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenCreate}
          sx={{ backgroundColor: "#2ea043", color: "#fff" }}
        >
          New Kit
        </Button>
      </Box>

      {loading ? (
        <CircularProgress sx={{ color: "#fff" }} />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <>
          {Object.entries(groupedByRoot).map(([rootName, kitsInThisRoot]) => (
            <Accordion
              key={rootName}
              sx={{
                backgroundColor: "#20232a",
                color: "#fff",
                mb: 2,
                "& .MuiAccordionSummary-content": {
                  alignItems: "center",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#A8B2D1" }} />}
              >
                <Typography sx={{ flexGrow: 1, fontWeight: 600 }}>
                  {rootName} ({kitsInThisRoot.length} kit
                  {kitsInThisRoot.length > 1 ? "s" : ""})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer
                  component={Paper}
                  sx={{
                    backgroundColor: "transparent",
                    borderRadius: 0,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: "#A8B2D1" }}>Serial</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Eyetracker Type</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Headset Status</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Created At</TableCell>
                        <TableCell sx={{ color: "#A8B2D1" }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {kitsInThisRoot.map((kit) => (
                        <TableRow key={kit.id} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                          <TableCell sx={{ color: "#fff" }}>{kit.serial}</TableCell>
                          <TableCell sx={{ color: "#fff" }}>
                            {kit.eyetracker_type || "—"}
                          </TableCell>
                          <TableCell sx={{ color: "#fff" }}>{kit.headset_status}</TableCell>
                          <TableCell sx={{ color: "#fff" }}>
                            {new Date(kit.created_at).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => handleOpenEdit(kit)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDeleteClick(kit.id)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}

          {kits.length === 0 && (
            <Typography sx={{ mt: 2, color: "#A8B2D1" }}>
              No VCAT kits available.
            </Typography>
          )}
        </>
      )}

      {/* Create / Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "#161B22", color: "#fff" }}>
          {isEdit ? "Edit VCat Kit" : "Create VCat Kit"}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#161B22" }}>
          <TextField
            label="Serial"
            name="serial"
            fullWidth
            margin="normal"
            value={formData.serial}
            onChange={handleTextFieldChange}
            sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
          />
          <TextField
            label="Eyetracker type"
            name="eyetracker_type"
            fullWidth
            margin="normal"
            value={formData.eyetracker_type}
            onChange={handleTextFieldChange}
            sx={{ input: { color: "#fff" }, label: { color: "#A8B2D1" } }}
          />

          <FormControl fullWidth margin="normal" sx={{ label: { color: "#A8B2D1" } }}>
            <InputLabel sx={{ color: "#A8B2D1" }}>Account Root</InputLabel>
            <Select
              value={formData.account_root_id}
              name="account_root_id"
              label="Account Root"
              onChange={handleSelectChange}
              sx={{ color: "#fff", ".MuiSvgIcon-root": { color: "#fff" } }}
            >
              {accountRoots.map((root) => (
                <MenuItem key={root.id} value={root.id}>
                  {root.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" sx={{ label: { color: "#A8B2D1" } }}>
            <InputLabel sx={{ color: "#A8B2D1" }}>Headset Status</InputLabel>
            <Select
              value={formData.headset_status}
              name="headset_status"
              label="Headset Status"
              onChange={handleSelectChange}
              sx={{ color: "#fff", ".MuiSvgIcon-root": { color: "#fff" } }}
            >
              {Object.values(KitStatusEnum).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions sx={{ backgroundColor: "#161B22", pb: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ color: "#A8B2D1" }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ backgroundColor: "#2ea043" }}>
            {isEdit ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete VCat Kit"
        message="Are you sure you want to delete this kit?"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Box>
  );
};

export default VCatKitsPage;
